/**
 * Booking API client (Iter 190) — services, availability & bookings.
 * Backend routes live under /api/barbers/* and /api/bookings/*.
 */
import { api } from '@/lib/api';
import { emitBookingUpdated } from '@/lib/bookingEvents';

// Envuelve una mutación: emite el evento global SOLO tras éxito confirmado.
const withBookingEvent = async (action, promise) => {
  const res = await promise;
  emitBookingUpdated({ action });
  return res;
};

export const bookingApi = {
  services(barberRef) {
    return api.get(`/barbers/${encodeURIComponent(barberRef)}/services`);
  },
  createService(barberRef, data) {
    return api.post(`/barbers/${encodeURIComponent(barberRef)}/services`, data);
  },
  updateService(barberRef, serviceId, data) {
    return api.patch(`/barbers/${encodeURIComponent(barberRef)}/services/${encodeURIComponent(serviceId)}`, data);
  },
  deleteService(barberRef, serviceId) {
    return api.delete(`/barbers/${encodeURIComponent(barberRef)}/services/${encodeURIComponent(serviceId)}`);
  },
  reorderServices(barberRef, ids) {
    return api.put(`/barbers/${encodeURIComponent(barberRef)}/services/order`, { ids });
  },
  availability(barberRef, date, serviceId, excludeBookingId) {
    const params = new URLSearchParams({ date });
    if (Array.isArray(serviceId)) params.set('service_ids', serviceId.join(','));
    else if (serviceId) params.set('service_id', serviceId);
    if (excludeBookingId) params.set('exclude_booking_id', excludeBookingId);
    return api.get(`/barbers/${encodeURIComponent(barberRef)}/availability?${params.toString()}`);
  },
  publicConfig(barberRef) {
    return api.get(`/barbers/${encodeURIComponent(barberRef)}/booking-config`);
  },
  // Protección v2 · B.2 — política de cancelación pública (perfil + revisión) y vista previa del barbero.
  cancellationPolicy(barberRef) {
    return api.get(`/barbers/${encodeURIComponent(barberRef)}/cancellation-policy`);
  },
  cancelFeesPreview() {
    return api.get('/settings/citas/pagos/cancel-fees');
  },
  daySchedule(barberRef, date) {
    return api.get(`/barbers/${encodeURIComponent(barberRef)}/day-schedule?date=${date}`);
  },
  monthSummary(barberRef, month) {
    return api.get(`/barbers/${encodeURIComponent(barberRef)}/month-summary?month=${month}`);
  },
  getMySettings() {
    return api.get('/booking-settings/me');
  },
  // LOTE 4 — bloqueos recurrentes y descansos
  createBlock(body) { return api.post('/bookings/blocks', body); },
  editBlock(id, body) { return api.patch(`/bookings/blocks/${id}`, body); },
  deleteBlock(id, { barber_id, scope = 'series', date } = {}) {
    const qs = new URLSearchParams({ scope });
    if (date) qs.set('date', date);
    if (barber_id) qs.set('barber_id', barber_id);
    return api.delete(`/bookings/blocks/${id}?${qs.toString()}`);
  },
  saveMySettings(partial) {
    return api.put('/booking-settings/me', partial);
  },
  create(payload) {
    return withBookingEvent('create', api.post('/bookings', payload));
  },
  createManual(payload) {
    return withBookingEvent('create_manual', api.post('/bookings/manual', payload));
  },
  clientsSearch(q, barberId = null) {
    return api.get(`/clients/search?q=${encodeURIComponent(q)}${barberId ? `&barber_id=${encodeURIComponent(barberId)}` : ''}`);
  },
  clientLookup(phone, barberId = null) {
    return api.get(`/clients/lookup?phone=${encodeURIComponent(phone)}${barberId ? `&barber_id=${encodeURIComponent(barberId)}` : ''}`);
  },
  createSale(bookingId, payload) {
    return withBookingEvent('create_sale', api.post(`/bookings/${bookingId}/sale`, payload));
  },
  getSale(bookingId) {
    return api.get(`/bookings/${bookingId}/sale`);
  },
  resendReceipt(bookingId) {
    return api.post(`/bookings/${bookingId}/receipt/resend`);
  },
  chargeFee(bookingId) {
    return withBookingEvent('charge_fee', api.post(`/bookings/${bookingId}/charge-fee`, {}));
  },
  undoNoShow(bookingId, refundFee = true) {
    return withBookingEvent('undo_no_show', api.post(`/bookings/${bookingId}/undo-no-show`, { refund_fee: refundFee }));
  },
  salesReport(from, to) {
    return api.get(`/settings/citas/pagos/sales-report?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);
  },
  listClients(q = '') {
    return api.get(`/clients${q ? `?q=${encodeURIComponent(q)}` : ''}`);
  },
  setClientTrusted(clientId, trusted) {
    return api.put(`/clients/${clientId}/trusted`, { trusted });
  },
  paymentPreview(barberRef, serviceId) {
    const q = Array.isArray(serviceId) ? `service_ids=${encodeURIComponent(serviceId.join(','))}` : `service_id=${encodeURIComponent(serviceId)}`;
    return api.get(`/bookings/payment-preview?barber=${encodeURIComponent(barberRef)}&${q}`);
  },
  checkout(payload) {
    return withBookingEvent('checkout', api.post('/bookings/checkout', payload));
  },
  continuePayment(bookingId, originUrl) {
    return withBookingEvent(
      'continue_payment',
      api.post(`/bookings/${encodeURIComponent(bookingId)}/continue-payment`, { origin_url: originUrl }),
    );
  },
  // Pago adelantado (Booksy Pay, jun-2026) — el cliente paga desde "Mis citas".
  prepayPreview(bookingId) {
    return api.get(`/bookings/${encodeURIComponent(bookingId)}/prepay-preview`);
  },
  prepayCheckout(bookingId, { tip_amount = 0, origin_url }) {
    return withBookingEvent(
      'prepay_checkout',
      api.post(`/bookings/${encodeURIComponent(bookingId)}/prepay-checkout`, { tip_amount, origin_url }),
    );
  },
  updateBookingStatus(bookingId, status) {
    return withBookingEvent(
      'status',
      api.patch(`/bookings/${encodeURIComponent(bookingId)}`, { status }),
    );
  },
  // CRÍTICO 21-oct-2026 — asignar cliente (ficha + cuenta) a una cita
  assignClient(bookingId, body) { return api.post(`/bookings/${bookingId}/assign-client`, body); },
  pendingAssign() { return api.get('/bookings/pending-assign'); },
  payLinkResend(bookingId, body) { return api.post(`/bookings/${bookingId}/pay-link/resend`, body); },
  confirmAssign(bookingId, clientRecordId) { return api.post(`/bookings/${bookingId}/confirm-assign`, { client_record_id: clientRecordId }); },
  calendarIcsUrl(bookingId) { return `${process.env.REACT_APP_BACKEND_URL}/api/bookings/${encodeURIComponent(bookingId)}/calendar.ics`; },
  editBooking(bookingId, changes) {
    return withBookingEvent('edit', api.patch(`/bookings/${encodeURIComponent(bookingId)}/edit`, changes));
  },
  rescheduleBooking(bookingId, date, startTime, durationMinutes = null, operation = 'move', allowOverlap = false) {
    const body = { date, start_time: startTime, operation };
    if (durationMinutes) body.duration_minutes = durationMinutes;
    if (allowOverlap) body.allow_overlap = true;
    return withBookingEvent(
      'reschedule',
      api.patch(`/bookings/${encodeURIComponent(bookingId)}/reschedule`, body),
    );
  },
  cascadePreview(bookingId, date, startTime, durationMinutes = null) {
    const body = { date, start_time: startTime };
    if (durationMinutes) body.duration_minutes = durationMinutes;
    return api.post(`/bookings/${encodeURIComponent(bookingId)}/cascade-preview`, body);
  },
  cascadeApply(bookingId, date, startTime, durationMinutes = null) {
    const body = { date, start_time: startTime };
    if (durationMinutes) body.duration_minutes = durationMinutes;
    return withBookingEvent(
      'cascade',
      api.post(`/bookings/${encodeURIComponent(bookingId)}/cascade-apply`, body),
    );
  },
  paymentsSettings() {
    return api.get('/settings/citas/pagos');
  },
  savePaymentsSettings(partial) {
    return api.put('/settings/citas/pagos', partial);
  },
  paymentsSummary() {
    return api.get('/settings/citas/pagos/summary');
  },
  paymentsTransactions(tab = 'transactions') {
    return api.get(`/settings/citas/pagos/transactions?tab=${tab}`);
  },
  paymentsServices() {
    return api.get('/settings/citas/pagos/services');
  },
  saveServicePaymentRule(serviceId, data) {
    return api.put(`/settings/citas/pagos/services/${encodeURIComponent(serviceId)}/rule`, data);
  },
  myBookings() {
    return api.get('/bookings/me');
  },
  myReceivedBookings(scope = 'upcoming') {
    return api.get(`/bookings/barber/me?scope=${scope}`);
  },
  paymentsEarnings() {
    return api.get('/settings/citas/pagos/earnings');
  },
  refundBooking(bookingId) {
    return withBookingEvent('refund', api.post(`/bookings/${encodeURIComponent(bookingId)}/refund`));
  },
  refundPreview(bookingId) {
    return api.get(`/bookings/${encodeURIComponent(bookingId)}/refund-preview`);
  },
  // Deploy 5 — Horario Premium + cancelación del cliente
  premiumRequest(payload) {
    return withBookingEvent('premium_request', api.post('/bookings/premium-request', payload));
  },
  premiumRequests() {
    return api.get('/bookings/premium-requests');
  },
  premiumApprove(bookingId) {
    return withBookingEvent('premium_approve', api.post(`/bookings/${encodeURIComponent(bookingId)}/premium/approve`, {}));
  },
  premiumReject(bookingId, reason) {
    return withBookingEvent('premium_reject', api.post(`/bookings/${encodeURIComponent(bookingId)}/premium/reject`, { reason: reason || null }));
  },
  cancelPreview(bookingId) {
    return api.get(`/bookings/${encodeURIComponent(bookingId)}/cancel-preview`);
  },
  clientCancel(bookingId) {
    return withBookingEvent('client_cancel', api.post(`/bookings/${encodeURIComponent(bookingId)}/client-cancel`, {}));
  },
  qrCharge(bookingId) {
    return api.post(`/bookings/${encodeURIComponent(bookingId)}/qr-charge`);
  },
};

// B2 — Admin (founder): comisión/retención + vista operativa de citas.
export const adminBookingPaymentsApi = {
  config() {
    return api.get('/admin/payments/booking-config');
  },
  saveConfig(data) {
    return api.put('/admin/payments/booking-config', data);
  },
  overview() {
    return api.get('/admin/payments/booking-overview');
  },
  processPayouts() {
    return api.post('/admin/payments/process-payouts');
  },
  balanceHealth() {
    return api.get('/admin/payments/stripe-balance-health');
  },
  webhookHealth() {
    return api.get('/admin/payments/webhook-health');
  },
  // SB53 — Retiro Express: comisión/límites + historial global.
  expressConfig() {
    return api.get('/admin/payments/express-config');
  },
  saveExpressConfig(data) {
    return api.put('/admin/payments/express-config', data);
  },
  expressPayouts() {
    return api.get('/admin/payments/express-payouts');
  },
  // SB64 — levantar la pausa automática de plataforma (Stripe instant_payouts_limit_exceeded).
  resumeExpress() {
    return api.post('/admin/payments/express-resume', {});
  },
  // sep-2026 — auditoría de duraciones (solo lectura; la corrección es por consola).
  durationAudit(barber) {
    return api.get(`/admin/bookings/duration-audit${barber ? `?barber=${encodeURIComponent(barber)}` : ''}`);
  },
};

// B2 — Cobro presencial por QR (PÚBLICO, el cliente no necesita cuenta).
export const qrPayApi = {
  info(token) {
    return api.get(`/pay/qr/${encodeURIComponent(token)}`);
  },
  checkout(token, originUrl) {
    return api.post(`/pay/qr/${encodeURIComponent(token)}/checkout`, { origin_url: originUrl });
  },
  status(token, sessionId) {
    const q = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
    return api.get(`/pay/qr/${encodeURIComponent(token)}/status${q}`);
  },
};

export default bookingApi;
